import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useReactFlow, useStore } from "reactflow";
import { styled } from "@mui/material/styles";
import MuiBox from "@mui/material/Box";
import MuiList from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Tooltip from "@mui/material/Tooltip";
import MuiListItemButton from "@mui/material/ListItemButton";
import MuiListItemIcon from "@mui/material/ListItemIcon";
import MuiListItemText from "@mui/material/ListItemText";
import {
  ThemedPivotFlipTableIcon,
  ThemedCenterFocusStrongIcon,
  ThemedMapIcon,
  ThemedRemoveIcon,
  ThemedAddIcon
} from "assets/CXFIcons/Designer/FlowControls";
import {
  toggleMinMap,
  toggleCanvasDirection,
  setViewOptions
} from "redux/actions/flowDesigner/settingsActionCreator";
import { setCanvasFlowInstance } from "redux/actions/flowDesigner/flowsActionCreator";
import { THEME_COLORS } from "constants/theme";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useDebouncer } from "hooks/useDebouncer";
import { getCanvasCenter } from "helpers/common";

const Box = styled(MuiBox)(() => ({
  display: "flex",
  flexDirection: "row",
  position: "absolute",
  bottom: 0,
  left: 0,
  right: "unset",
  top: "unset",
  padding: "15px",
  zIndex: 6
}));

const List = styled(MuiList)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  backgroundColor: THEME_COLORS[theme.palette.mode.toUpperCase()].SECONDARY_BG,
  borderRadius: "9px",
  padding: "6px"
}));

const ListItemButton = styled(MuiListItemButton)(({ theme }) => ({
  padding: "8px",
  borderRadius: "4px",
  "&:hover": {
    backgroundColor: THEME_COLORS[theme.palette.mode.toUpperCase()].SELECTED_BG
  }
}));

const ListItemIcon = styled(MuiListItemIcon)(({ theme }) => ({
  minWidth: "auto",
  "& svg": {
    fontSize: "18px !important",
    color: THEME_COLORS[theme.palette.mode.toUpperCase()].PRIMARY_FONT
  }
}));

const ListItemText = styled(MuiListItemText)(() => ({
  "& .MuiListItemText-primary": {
    fontSize: "13px",
    padding: "0 3px",
    whiteSpace: "nowrap",
    width: "42px",
    textAlign: "center"
  }
}));

const PercentageZoomInput = styled("input")({
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0
  },
  MozAppearance: "textfield"
});

const FlowControl = ({ title, action, children }) => {
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={action}>
        <Tooltip title={title} placement="top">
          <ListItemIcon>{children}</ListItemIcon>
        </Tooltip>
      </ListItemButton>
    </ListItem>
  );
};

const FlowControls = () => {
  const dispatch = useDispatch();
  const zoomLevel = useStore((store) => store.transform[2]);
  const { toObject, zoomTo, getZoom, setCenter } = useReactFlow();
  const { t } = useTranslation("flowDesigner");
  const [openInputZoomLevel, setOpenInputZoomLevel] = useState(false);
  const [customZoomLevel, setCustomZoomLevel] = useState(Math.round(getZoom() * 100));
  const { themeMode } = useSelector((store) => store?.portal?.settings);
  const { debouncer } = useDebouncer(handleUpdateZoomPreference, 500);
  const hasInitialized = useRef(false);
  const settings = useSelector((store) => store?.designer?.settings);

  async function handleUpdateZoomPreference(zoom) {
    dispatch(
      setViewOptions({
        ...settings.viewOptions,
        zoomLevel: zoom
      })
    );
  }

  const handleZoomIn = () => {
    if (openInputZoomLevel) {
      setOpenInputZoomLevel(false);
    }
    const currentZoom = getZoom();
    zoomTo(currentZoom + 0.05);
    saveFlowInstance();
  };

  const handleZoomOut = () => {
    if (openInputZoomLevel) {
      setOpenInputZoomLevel(false);
    }
    const currentZoom = getZoom();
    zoomTo(currentZoom - 0.05);
    saveFlowInstance();
  };

  const saveFlowInstance = () => {
    setTimeout(() => dispatch(setCanvasFlowInstance(toObject())), 300);
  };

  const handleOpenInputZoomLevel = () => {
    setCustomZoomLevel(Math.round(getZoom() * 100));
    setOpenInputZoomLevel(true);
  };

  const handleCloseInputZoomLevel = () => {
    if ((customZoomLevel / 100).toFixed(2) !== getZoom().toFixed(2)) {
      zoomTo((customZoomLevel / 100).toFixed(2));
    } else {
      zoomTo(getZoom());
    }
    setOpenInputZoomLevel(false);
  };

  const handleChange = (e) => {
    setCustomZoomLevel(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCloseInputZoomLevel();
    }
  };

  const centerCanvas = () => {
    const { centerX, centerY } = getCanvasCenter(toObject().nodes);

    setCenter(centerX, centerY, { zoom: getZoom(), duration: 400 });
  };

  useEffect(() => {
    if (!hasInitialized.current) {
      setTimeout(() => {
        hasInitialized.current = true;
      }, 1000);
      return;
    }

    debouncer(zoomLevel);
  }, [zoomLevel]);

  if (!settings.viewOptions.showControls) return <></>;

  return (
    <Box className="flow-controls">
      <List>
        <FlowControl
          title={t("tooltips.switch_flow_orientation")}
          action={() => {
            dispatch(toggleCanvasDirection());
          }}
        >
          <ThemedPivotFlipTableIcon />
        </FlowControl>
        <FlowControl
          title={t("tooltips.center_canvas")}
          action={() => {
            centerCanvas();
          }}
        >
          <ThemedCenterFocusStrongIcon />
        </FlowControl>
        <FlowControl
          title={t("tooltips.mini_map")}
          action={() => {
            dispatch(toggleMinMap());
          }}
        >
          <ThemedMapIcon />
        </FlowControl>
        <FlowControl title={t("tooltips.zoom_out")} action={handleZoomOut}>
          <ThemedRemoveIcon />
        </FlowControl>
        {!openInputZoomLevel && (
          <ListItem disablePadding onClick={handleOpenInputZoomLevel}>
            <ListItemText primary={`${Math.round(zoomLevel * 100)} %`} />
          </ListItem>
        )}
        {openInputZoomLevel && (
          <ListItem disablePadding>
            <ClickAwayListener onClickAway={handleCloseInputZoomLevel}>
              <PercentageZoomInput
                value={customZoomLevel}
                type="number"
                onChange={(e) => handleChange(e)}
                onKeyDown={(e) => handleKeyDown(e)}
                style={{
                  width: "60px",
                  backgroundColor:
                    themeMode === "dark" ? THEME_COLORS.DARK.INPUT_BG : THEME_COLORS.LIGHT.INPUT_BG,
                  border: themeMode === "dark" ? "none" : "1px solid #d3dae4",
                  borderRadius: "3px",
                  height: "25px",
                  textAlign: "center"
                }}
              />
            </ClickAwayListener>
          </ListItem>
        )}
        <FlowControl title={t("tooltips.zoom_in")} action={handleZoomIn}>
          <ThemedAddIcon />
        </FlowControl>
      </List>
    </Box>
  );
};

export default FlowControls;
